import React from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { GripVertical, X } from 'lucide-react';
import { uniqueId } from 'lodash';
import { DEFAULT_FEATURE_ICON } from '@repo/lib';

import Button from '@dashboard/components/Button';
import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import Reorderable from '@dashboard/components/Reorderable';
import { cn } from '@dashboard/lib/utils';

import HighlightField from './HighlightField';

// The card renders a line whole beside its icon, so a long one wraps the list
// out of shape; the same ceiling as the card's other single lines.
export const FEATURE_LIMIT = 120;

// Rows need a stable key of their own to be dragged; it never leaves the form.
export const newFeature = (feature = {}) => ({
  key: uniqueId('feature-'),
  text: feature.text ?? '',
  icon: feature.icon ?? null,
});

/**
 * The bump's "what you get" list: a line per row, each leading with the icon
 * picker the highlight uses, with a handle to reorder and a cross to remove.
 * Blank rows are dropped on save, so the three a new bump starts with cost
 * nothing if they are left alone.
 */
export default function FeaturesField({ name = 'features' }) {
  const { values, setFieldValue } = useFormikContext();
  const rows = values[name] ?? [];

  return (
    <FieldArray
      name={name}
      render={arrayHelpers => (
        <div>
          <Reorderable.Wrapper>
            <Reorderable
              id={`fields.${name}`}
              items={rows}
              onReorder={items => setFieldValue(name, items)}
              renderItem={({ dragHandleProps, index, isDragging }) => (
                <Reorderable.Item
                  isDragging={isDragging}
                  border={false}
                  className="flex items-center gap-1 pb-2"
                >
                  <div
                    {...dragHandleProps}
                    aria-label={`Reorder feature ${index + 1}`}
                    className={cn(
                      buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
                      '-ml-1.5 shrink-0 cursor-grab active:cursor-grabbing',
                    )}
                  >
                    <GripVertical className="size-4" />
                  </div>

                  <div className="min-w-0 flex-1">
                    <HighlightField
                      name={`${name}.${index}.text`}
                      iconName={`${name}.${index}.icon`}
                      fallbackIcon={DEFAULT_FEATURE_ICON}
                      pickerLabel="Feature icon"
                      ariaLabel={`Feature ${index + 1}`}
                      placeholder="e.g. Free shipping"
                      maxLength={FEATURE_LIMIT}
                    />
                  </div>

                  <ShadcnButton
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Remove feature ${index + 1}`}
                    className="-mr-1.5 shrink-0"
                    onClick={() => arrayHelpers.remove(index)}
                  >
                    <X className="size-4" />
                  </ShadcnButton>
                </Reorderable.Item>
              )}
            />
          </Reorderable.Wrapper>

          <Button
            type="button"
            appearance="light"
            size="small"
            handleClick={() => arrayHelpers.push(newFeature())}
          >
            Add feature
          </Button>
        </div>
      )}
    />
  );
}
